/**
 * routineRows — the ONE routine-row builder (routine list parity, 2026-08-02;
 * multi-routine v2, 2026-08-16).
 *
 * Promoted out of RoutineManagerPanel so BOTH routine surfaces — the left
 * sidebar's "routines" section and the manager panel — derive their rows from
 * this single pure function over the SAME inputs (the live `threads` array +
 * the shared `listRoutines()` snapshot owned by useAccountRoutines). That is
 * the parity directive made structural: same membership, same count, always.
 *
 * v2 PARITY RULE (a thread can hold several routines): the unit of truth is
 * the ROUTINE — buildRoutineManagerRows returns one row PER ROUTINE, keyed by
 * routineId, and both surfaces count routines identically. The MANAGER
 * renders the rows flat; the SIDEBAR groups them by thread
 * (groupRoutineRowsByThread) because its rail lists THREADS — same
 * membership, two projections.
 *
 * Row semantics:
 *   • SERVER rows — every routine in the `listRoutines()` snapshot (the
 *     authoritative per-routine source since v2), ordered by their thread's
 *     `orderThreads` position (THE shared display rule) then name, threads
 *     outside the live index window trailing deterministically;
 *   • THREAD-fallback rows — routine threads (`routineId` mirror present)
 *     not covered by any server row (no snapshot yet, or a failed fetch with
 *     no prior success): a single primary row synthesized from the thread's
 *     aggregate mirror, so the rail renders immediately in every state.
 * Every row the server list returns is a LIVE routine by contract (the
 * endpoint heals/excludes archived-or-missing-thread routines server-side).
 */
import type { ChatThread } from './types';
import { orderThreads } from './useChatThreads';
import { routineThreadDisplayName } from './RoutineGlyph';
import type { RoutineSummary } from '../../services/routines/client';

/**
 * One row of a routine surface — ONE ROUTINE (v2). `routineId` is the action
 * key (pause/resume/cancel); `threadId` the navigation key (open thread) and
 * grouping key (several rows may share it).
 */
export interface RoutineManagerRowView {
  routineId: string;
  threadId: string;
  name: string;
  /**
   * The thread's own title, shown as a quiet secondary hint when it still
   * differs from the routine name (naming coherence, 2026-08-01). Absent for
   * rows with no local thread record and for converged/identical titles.
   */
  threadTitle?: string;
  paused: boolean;
  /** 'trigger' rows fire on platform events (no clock — nextRunAt stays
   * null and scheduleSummary is the trigger summary). */
  kind: 'schedule' | 'trigger';
  scheduleSummary?: string;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  lastRunStatus?: 'ok' | 'error';
  /**
   * 'server' — from the authoritative listRoutines() snapshot; 'thread' —
   * synthesized from the thread's aggregate mirror (snapshot gap fallback).
   */
  source: 'thread' | 'server';
}

/** One sidebar rail entry: a THREAD holding one or more routine rows. */
export interface RoutineThreadGroupView {
  threadId: string;
  rows: RoutineManagerRowView[];
  /** The group's display driver — its first row, which within-thread
   * ordering makes the backend-primary (soonest active run, else an active
   * trigger, else the first paused). */
  primary: RoutineManagerRowView;
}

/** Epoch-ms → ISO at the wire boundary; null for absent/invalid (never renders garbage). */
export function msToIso(ms: number | null | undefined): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return null;
  return new Date(ms).toISOString();
}

function serverRowView(summary: RoutineSummary, thread?: ChatThread): RoutineManagerRowView {
  const name = summary.name || 'Routine';
  return {
    routineId: summary.routineId,
    threadId: summary.threadId,
    name,
    threadTitle: thread && thread.title && thread.title !== name ? thread.title : undefined,
    paused: summary.status === 'paused',
    kind: summary.kind === 'trigger' ? 'trigger' : 'schedule',
    scheduleSummary: summary.scheduleSummary,
    nextRunAt: summary.kind === 'trigger' ? null : msToIso(summary.nextRunAtMs),
    lastRunAt: msToIso(summary.lastRunAtMs),
    lastRunStatus: summary.lastRunStatus ?? undefined,
    source: 'server',
  };
}

/**
 * Union the two row sources (pure, shared by the sidebar section AND the
 * manager panel — see module doc). Server rows are the per-routine truth;
 * ordering follows the row's thread in `orderThreads` order (the shared
 * display rule), rows within a thread by name then routineId, and rows whose
 * thread is outside the live window trail deterministically (name, then
 * routineId). Routine threads absent from the snapshot fall back to one
 * mirror-synthesized row so the rail never blocks on the network.
 */
export function buildRoutineManagerRows(
  threads: ChatThread[],
  serverRoutines: RoutineSummary[] | null,
): RoutineManagerRowView[] {
  const threadsById = new Map(threads.map((t) => [t.id, t] as const));
  const orderedThreadIds = orderThreads(threads).map((t) => t.id);
  const threadRank = new Map(orderedThreadIds.map((id, i) => [id, i] as const));

  const serverRows = (serverRoutines ?? [])
    .filter((s) => !!s.threadId && !!s.routineId)
    .map((s) => serverRowView(s, threadsById.get(s.threadId)));
  // Within a thread, rows sort by the BACKEND's primary rule (active with the
  // soonest next run, then active triggers, then paused) so a group's first
  // row — the sidebar's display driver — agrees with the mirror's primary
  // (2026-08-16 review: name-ordering made the rail render a paused
  // routine's blank cadence while the header showed the soonest run).
  const primaryRank = (r: RoutineManagerRowView): number => {
    if (!r.paused && r.nextRunAt) return 0;
    if (!r.paused) return 1;
    return 2;
  };
  serverRows.sort((a, b) => {
    const rankA = threadRank.get(a.threadId);
    const rankB = threadRank.get(b.threadId);
    if (rankA !== undefined && rankB !== undefined && rankA !== rankB) return rankA - rankB;
    if (rankA !== undefined && rankB === undefined) return -1;
    if (rankA === undefined && rankB !== undefined) return 1;
    if (a.threadId !== b.threadId) return a.threadId.localeCompare(b.threadId);
    const pa = primaryRank(a);
    const pb = primaryRank(b);
    if (pa !== pb) return pa - pb;
    if (pa === 0 && a.nextRunAt !== b.nextRunAt) {
      return String(a.nextRunAt).localeCompare(String(b.nextRunAt));
    }
    return a.name.localeCompare(b.name) || a.routineId.localeCompare(b.routineId);
  });

  const coveredThreadIds = new Set(serverRows.map((r) => r.threadId));
  const fallbackRows: RoutineManagerRowView[] = orderThreads(threads)
    .filter((t) => !!t.routineId && !coveredThreadIds.has(t.id))
    .map((t): RoutineManagerRowView => ({
      routineId: t.routineId as string,
      threadId: t.id,
      name: routineThreadDisplayName(t),
      threadTitle:
        t.routineName && t.title && t.title !== routineThreadDisplayName(t) ? t.title : undefined,
      paused: t.routineStatus === 'paused',
      // The mirror does not carry a kind; an active thread with a summary but
      // no next run is trigger-shaped, which only affects the meta line.
      kind: t.routineStatus !== 'paused' && t.routineNextRunAt == null && t.routineScheduleSummary
        ? 'trigger'
        : 'schedule',
      scheduleSummary: t.routineScheduleSummary,
      nextRunAt: t.routineNextRunAt,
      lastRunAt: t.routineLastRunAt,
      lastRunStatus: t.routineLastRunStatus,
      source: 'thread',
    }));

  return [...serverRows, ...fallbackRows].sort((a, b) => {
    const rankA = threadRank.get(a.threadId);
    const rankB = threadRank.get(b.threadId);
    if (rankA !== undefined && rankB !== undefined && rankA !== rankB) return rankA - rankB;
    if (rankA !== undefined && rankB === undefined) return -1;
    if (rankA === undefined && rankB !== undefined) return 1;
    if (a.threadId !== b.threadId) return a.threadId.localeCompare(b.threadId);
    return 0; // stable: preserve within-thread order established above
  });
}

/**
 * The sidebar projection: group per-routine rows by THREAD, preserving row
 * order (so groups sit in `orderThreads` order and each group's first row is
 * its primary). Pure and deterministic — the rail's row list.
 */
export function groupRoutineRowsByThread(rows: RoutineManagerRowView[]): RoutineThreadGroupView[] {
  const groups = new Map<string, RoutineManagerRowView[]>();
  for (const row of rows) {
    const list = groups.get(row.threadId);
    if (list) list.push(row);
    else groups.set(row.threadId, [row]);
  }
  return [...groups.entries()].map(([threadId, groupRows]) => ({
    threadId,
    rows: groupRows,
    primary: groupRows[0],
  }));
}
